import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

/**
 * CollapsibleCard component for toggling visibility of content.
 *
 * Card + Collapse rather than Accordion: the header keeps a dedicated action slot for
 * `headerButton`, and only the title area toggles, so a header button never opens the card.
 */

interface CollapsibleCardProps {
  title: string;
  subTitle?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  headerButton?: React.ReactNode;
  allowToggle?: boolean;
  footer?: React.ReactNode;
  style?: React.CSSProperties;
  bodyBackgroundColor?: string;
  onOpen?: () => void;
}

const CollapsibleCard = ({
  title,
  subTitle,
  children,
  defaultOpen = false,
  headerButton,
  allowToggle = true,
  footer,
  bodyBackgroundColor = '',
  style,
  onOpen = () => {},
}: CollapsibleCardProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen || !allowToggle);

  useEffect(() => {
    setIsOpen(defaultOpen || !allowToggle);
  }, [defaultOpen, allowToggle]);

  const toggle = () => setIsOpen(allowToggle ? !isOpen : true);

  return (
    <Card style={style}>
      <CardHeader
        avatar={
          allowToggle ? (
            <IconButton
              size='small'
              aria-label={isOpen ? 'Collapse' : 'Expand'}
              aria-expanded={isOpen}
              onClick={toggle}
            >
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          ) : undefined
        }
        title={
          <Box
            onClick={toggle}
            sx={{ display: 'flex', alignItems: 'baseline', gap: 2, cursor: allowToggle ? 'pointer' : 'default' }}
          >
            <Typography variant='h6' component='span'>
              {title}
            </Typography>
            {subTitle && (
              <Typography variant='body2' color='text.secondary' component='span'>
                {subTitle}
              </Typography>
            )}
          </Box>
        }
        action={headerButton}
        slotProps={{ action: { sx: { alignSelf: 'center', m: 0 } } }}
      />
      <Collapse in={isOpen} onEntered={onOpen}>
        <CardContent sx={{ bgcolor: bodyBackgroundColor || undefined }}>{children}</CardContent>
      </Collapse>
      {footer && <CardActions>{footer}</CardActions>}
    </Card>
  );
};

export default CollapsibleCard;
